"use client"

import type { TranslateFn } from '@open-mercato/shared/lib/i18n/context'
import type { ColumnDef } from '@tanstack/react-table'
import type { FilterDef } from '@open-mercato/ui/backend/FilterBar'
import { Badge } from '@open-mercato/ui/primitives/badge'
import { LEG_STATUSES, type LegStatus } from '../lib/lookups'

export type LegListItem = {
  id: string
  cargoId: string | null
  originLocationId: string | null
  destinationLocationId: string | null
  requiredVehicleType: string | null
  requiredCapabilities: string[]
  requiredQualifications: string[]
  scheduledPickupAt: string | null
  scheduledDeliveryAt: string | null
  status: LegStatus
  notes: string | null
  createdAt: string
  updatedAt: string
}

export type LegsListResponse = {
  items: LegListItem[]
  page: number
  pageSize: number
  total: number
  totalPages: number
}

function getLegStatusVariant(status: LegStatus): 'default' | 'secondary' | 'destructive' | 'muted' | 'outline' {
  switch (status) {
    case 'CREATED': return 'secondary'
    case 'ASSIGNED': return 'default'
    case 'IN_PROGRESS': return 'default'
    case 'COMPLETED': return 'outline'
    case 'CANCELLED': return 'muted'
    default: return 'secondary'
  }
}

function formatDateTime(value: string | null): string {
  if (!value) return '—'
  const d = new Date(value)
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
}

export function buildLegsColumns(t: TranslateFn): ColumnDef<LegListItem>[] {
  return [
    {
      id: 'status',
      accessorKey: 'status',
      header: () => t('dispatch.table.columns.status', 'Status'),
      cell: ({ row }) => {
        const status = row.original.status as LegStatus
        const label = t(`dispatch.lookups.legStatus.${status}`, status)
        return <Badge variant={getLegStatusVariant(status)}>{label}</Badge>
      },
    },
    {
      id: 'cargoId',
      accessorKey: 'cargoId',
      header: () => t('dispatch.table.columns.cargoId', 'Cargo ID'),
      cell: ({ row }) => row.original.cargoId ?? '—',
    },
    {
      id: 'requiredVehicleType',
      accessorKey: 'requiredVehicleType',
      header: () => t('dispatch.table.filters.vehicleType', 'Vehicle type'),
      cell: ({ row }) => row.original.requiredVehicleType ?? '—',
    },
    {
      id: 'scheduledPickupAt',
      accessorKey: 'scheduledPickupAt',
      header: () => t('dispatch.table.columns.scheduledPickupAt', 'Scheduled pickup'),
      cell: ({ row }) => formatDateTime(row.original.scheduledPickupAt),
    },
    {
      id: 'scheduledDeliveryAt',
      accessorKey: 'scheduledDeliveryAt',
      header: () => t('dispatch.table.columns.scheduledDeliveryAt', 'Scheduled delivery'),
      cell: ({ row }) => formatDateTime(row.original.scheduledDeliveryAt),
    },
  ]
}

export function buildLegsFilters(t: TranslateFn): FilterDef[] {
  return [
    {
      id: 'status',
      label: t('dispatch.table.filters.status', 'Status'),
      type: 'select',
      placeholder: t('dispatch.table.filters.statusPlaceholder', 'Filter by status'),
      options: LEG_STATUSES.map((s) => ({
        value: s,
        label: t(`dispatch.lookups.legStatus.${s}`, s),
      })),
    },
    {
      id: 'vehicleType',
      label: t('dispatch.table.filters.vehicleType', 'Vehicle type'),
      type: 'select',
      placeholder: t('dispatch.table.filters.vehicleTypePlaceholder', 'Filter by type'),
      options: [
        { value: 'TRUCK', label: 'Truck' },
        { value: 'SOLO', label: 'Solo' },
        { value: 'TRAILER', label: 'Trailer' },
      ],
    },
  ]
}
